import React from 'react';
import './concept-card-end-screen.scss';
import MeritApi from '../../apis/merit-api';
import SharedProfile from '../../../shared-storage/profile';
import history from '../routes/history';
import Path from '../routes/routes-path';

export const ConceptCelebrationHeader = ({ chapterTitle, conceptTitle, isDirectChapterPdf }) => (
    <div className="celebration-header">
        <div className="decorative-dots"></div>
        <div className="chapter-tag">{chapterTitle || 'Mathematics'}</div>
        <h1>{isDirectChapterPdf ? 'Chapter Completed! 🎉' : 'Concept Completed! 🎉'}</h1>
        <p>Great work mastering <strong>{isDirectChapterPdf ? (chapterTitle || 'this Chapter') : (conceptTitle || 'this Concept')}</strong></p>
    </div>
);

export const ConceptMeritCard = ({ merits = 50, isDirectChapterPdf, attemptNumber = 1 }) => {
    const [displayMerits, setDisplayMerits] = React.useState(0);

    React.useEffect(() => {
        if (!merits) {
            setDisplayMerits(0);
            return;
        }

        let startTimestamp = null;
        const duration = 1500;

        const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 2);
            setDisplayMerits(Math.floor(easeOut * merits));

            if (progress < 1) {
                window.requestAnimationFrame(step);
            } else {
                setDisplayMerits(merits);
            }
        };

        window.requestAnimationFrame(step);
    }, [merits]);

    return (
        <div className="cw-card merit-card">
            <div className="card-header">
                ★ MERITS EARNED
            </div>
            <div className="card-body">
                <div className="circle-tr"></div>
                <div className="circle-bl"></div>
                {attemptNumber >= 2 ? (
                    <div className="merit-message">
                        You are already rewarded for this {isDirectChapterPdf ? 'Chapter' : 'Concept Card'}
                    </div>
                ) : (
                    <>
                        <div className="merit-number">+{displayMerits}</div>
                        <div className="merit-label">MERITS</div>
                    </>
                )}
                <div className="accuracy-pill">
                    <span>✓</span> {isDirectChapterPdf ? 'Chapter Completed' : 'Concept Card Completed'}
                </div>
            </div>
        </div>
    );
};

export const ConceptSummaryCard = ({ conceptTitle, chapterTitle, timeTaken, totalPages, isDirectChapterPdf }) => (
    <div className="cw-card concept-summary-card">
        <div className="card-header">
            ▩ ACTIVITY SUMMARY
        </div>
        <div className="card-body">
            <div className="stat-cell">
                <div className="stat-label">{isDirectChapterPdf ? 'Chapter' : 'Concept'}</div>
                <div className="stat-value highlight-text">{isDirectChapterPdf ? (chapterTitle || 'Chapter') : (conceptTitle || 'Concept')}</div>
            </div>
            <div className="stat-cell">
                <div className="stat-label">Status</div>
                <div className="stat-value status-completed">Completed</div>
            </div>
            {totalPages ? (
                <div className="stat-cell">
                    <div className="stat-label">Pages Read</div>
                    <div className="stat-value">{totalPages} / {totalPages}</div>
                </div>
            ) : null}
            {timeTaken ? (
                <div className="stat-cell">
                    <div className="stat-label">Time Spent</div>
                    <div className="stat-value">{timeTaken}</div>
                </div>
            ) : null}
        </div>
    </div>
);

export const ConceptLeaderboardCard = ({ topStudents, studentRank, studentMerits, studentName = "You", onViewFullLeaderboard }) => {
    const hasCurrentUserInList = topStudents && topStudents.some(student => student.isCurrentUser);

    return (
        <div className="cw-card leaderboard-card">
            <div className="card-header">
                ⚡ CLASS LEADERBOARD — THIS MONTH
            </div>
            <div className="card-body">
                <div className="lb-header-row">
                    <div className="lb-rank-hdr">Rank</div>
                    <div className="lb-name-hdr">Name</div>
                    <div className="lb-merits-hdr">Merits</div>
                </div>

                {topStudents && topStudents.map((student, index) => {
                    const isCurrentUser = student.isCurrentUser;
                    let rankClass = 'lb-standard';
                    if (student.rank === 1) rankClass = 'lb-gold';
                    else if (student.rank === 2) rankClass = 'lb-silver';
                    else if (student.rank === 3) rankClass = 'lb-bronze';

                    const rowClass = `lb-row ${rankClass}${isCurrentUser ? ' lb-current-user' : ''}`;
                    return (
                        <div key={student.rank || index} className={rowClass}>
                            <div className="lb-rank-col">
                                <div className="lb-rank">{student.rank || (index + 1)}</div>
                            </div>
                            <div className="lb-name">{student.name}{isCurrentUser ? ' (You)' : ''}</div>
                            <div className="lb-merits">{student.merits}</div>
                        </div>
                    );
                })}

                {!hasCurrentUserInList && (
                    <div className="lb-row lb-standard lb-current-user">
                        <div className="lb-rank-col">
                            <div className="lb-rank">{studentRank}</div>
                        </div>
                        <div className="lb-name">{studentName} (You)</div>
                        <div className="lb-merits">{studentMerits}</div>
                    </div>
                )}

                <div className="lb-view-full" onClick={onViewFullLeaderboard} style={{ cursor: "pointer" }}>
                    View full leaderboard →
                </div>
            </div>
        </div>
    );
};

export const ChapterProgressSection = ({ totalConcepts, clearedConcepts, nextConceptTitle, hasNextConcept }) => {
    const total = totalConcepts || 1;
    const cleared = clearedConcepts || 1;
    const percentage = Math.min(100, Math.round((cleared / total) * 100));

    return (
        <div className="chapter-progress-section">
            <div className="progress-header">
                ✎ CHAPTER PROGRESS
            </div>
            <div className="progress-body">
                <div className="progress-info-row">
                    <div className="progress-title-col">
                        <span className="progress-heading">Progress Overview</span>
                        <span className="progress-subtext">{cleared} of {total} Concepts Completed ({percentage}%)</span>
                    </div>
                    {hasNextConcept && nextConceptTitle ? (
                        <div className="next-concept-preview">
                            <span className="next-tag">UP NEXT</span>
                            <span className="next-title">{nextConceptTitle}</span>
                        </div>
                    ) : (
                        <div className="next-concept-preview all-done">
                            <span className="next-tag-done"> CHAPTER COMPLETED</span>
                        </div>
                    )}
                </div>

                <div className="progress-bar-track">
                    <div className="progress-bar-fill" style={{ width: `${percentage}%` }}></div>
                </div>
            </div>
        </div>
    );
};

export default class ConceptCardEndScreen extends React.Component {
    constructor(props) {
        super(props);
        this.userInfo = SharedProfile.getProfile();
        this.state = {
            meritsEarned: this.props.metaInfo?.meritsEarned || 50,
            studentMerits: 0,
            studentRank: '-',
            leaderboardData: [],
            isLoading: true
        };
    }

    componentDidMount() {
        this.loadLeaderboardData();
    }

    loadLeaderboardData() {
        Promise.all([
            MeritApi.getStudentMerits(),
            MeritApi.getStudentMeritLeaderboard({ limit: 4, category: 'Top Performer', subjectId: this.userInfo ? this.userInfo.subjectId : undefined })
        ]).then(([meritsRes, leaderboardRes]) => {
            const studentMerits = Number((meritsRes.data?.totalActivityMerits || 0).toFixed(2));
            const studentRank = meritsRes.data?.topPerformerRank ?? meritsRes.data?.topPerfromerRank ?? '-';

            let leaderboardData = [];
            const rawLeaderboard = leaderboardRes.data;
            if (rawLeaderboard && Array.isArray(rawLeaderboard)) {
                leaderboardData = rawLeaderboard.slice(0, 4).map((lb, index) => ({
                    rank: lb.rank || (index + 1),
                    name: lb.name || "Student",
                    merits: Number((lb.finalMerits ?? lb.merits ?? 0).toFixed(2)),
                    isCurrentUser: lb.isCurrentUser || false
                }));
            }

            this.setState({
                studentMerits,
                studentRank,
                leaderboardData,
                isLoading: false
            });
        }).catch(err => {
            console.error("Error loading merits for concept end screen:", err);
            this.setState({ isLoading: false });
        });
    }

    handleViewFullLeaderboard = () => {
        history.push(Path.LEADERBOARD);
    };

    render() {
        const { metaInfo, onNextConcept, onBackToConcepts, onBrowseChapters } = this.props;
        const {
            conceptTitle = "Concept Card",
            chapterTitle = "Chapter",
            timeTaken = "",
            totalPages = 0,
            totalConcepts = 0,
            clearedConcepts = 0,
            nextConceptTitle = null,
            hasNextConcept = false,
            isDirectChapterPdf = false,
            attemptNumber = 1
        } = metaInfo || {};

        const { meritsEarned, studentMerits, studentRank, leaderboardData } = this.state;
        const studentName = this.userInfo?.name || "You";

        return (
            <div className="concept-card-end-screen">
                <div className="end-screen-inner-container">
                    <div className="breadcrumb">
                        {(metaInfo?.calledFrom === 'EXAM_PREP_KIT' || metaInfo?.calledFrom === 'EXAM_PREP_KIT_V2' || metaInfo?.from === 'EXAM_PREP_KIT_V2') ? (
                            <>
                                <span className="title-1 cursor-pointer" onClick={() => history.push(Path.HOME)}>{`Home >`}</span>
                                <span className="title-1 cursor-pointer pl-2" onClick={() => {
                                    if (onBrowseChapters) onBrowseChapters();
                                }}>{`${chapterTitle} >`}</span>
                                <span className="title-1 cursor-pointer pl-2" onClick={() => {
                                    if (onBrowseChapters) onBrowseChapters();
                                }}>{`Exam Prep Kit >`}</span>
                                <span className="title-1 cursor-pointer pl-2" onClick={() => {
                                    if (onBrowseChapters) onBrowseChapters();
                                }}>{`Table of Contents >`}</span>
                                <span className="pl-2 active">{isDirectChapterPdf ? chapterTitle : conceptTitle}</span>
                            </>
                        ) : (
                            <>
                                <span className="title-1 cursor-pointer" onClick={() => {
                                    if (isDirectChapterPdf) {
                                        history.push(Path.EXAM_PREP_KIT);
                                    } else if (onBrowseChapters) {
                                        onBrowseChapters();
                                    }
                                }}>{`Browse Chapters >`}</span>
                                {!isDirectChapterPdf && (
                                    <span className="title-1 cursor-pointer pl-2" onClick={onBackToConcepts}>{`${chapterTitle} >`}</span>
                                )}
                                <span className="pl-2 active">{isDirectChapterPdf ? chapterTitle : conceptTitle}</span>
                            </>
                        )}
                    </div>

                    <ConceptCelebrationHeader
                        chapterTitle={chapterTitle}
                        conceptTitle={conceptTitle}
                        isDirectChapterPdf={isDirectChapterPdf}
                    />

                    <div className="cards-grid">
                        <ConceptMeritCard
                            merits={meritsEarned}
                            isDirectChapterPdf={isDirectChapterPdf}
                            attemptNumber={attemptNumber}
                        />

                        <ConceptSummaryCard
                            conceptTitle={conceptTitle}
                            chapterTitle={chapterTitle}
                            timeTaken={timeTaken}
                            totalPages={totalPages}
                            isDirectChapterPdf={isDirectChapterPdf}
                        />

                        <ConceptLeaderboardCard
                            topStudents={leaderboardData}
                            studentRank={studentRank}
                            studentMerits={studentMerits}
                            studentName={studentName}
                            onViewFullLeaderboard={this.handleViewFullLeaderboard}
                        />
                    </div>

                    {!isDirectChapterPdf && (
                        <ChapterProgressSection
                            totalConcepts={totalConcepts}
                            clearedConcepts={clearedConcepts}
                            nextConceptTitle={nextConceptTitle}
                            hasNextConcept={hasNextConcept}
                        />
                    )}

                    <div className="action-buttons-container">
                        {hasNextConcept ? (
                            <button className="primary-btn" onClick={onNextConcept}>
                                Continue to Next Concept →
                            </button>
                        ) : null}

                        {isDirectChapterPdf ? (
                            <button className="primary-btn" onClick={() => {
                                if (onBrowseChapters) {
                                    onBrowseChapters();
                                } else if (metaInfo?.calledFrom === 'EXAM_PREP_KIT_V2' || metaInfo?.from === 'EXAM_PREP_KIT_V2') {
                                    if (window.history && window.history.length > 1) {
                                        history.goBack();
                                    } else {
                                        history.push(Path.HOME);
                                    }
                                } else {
                                    history.push(Path.EXAM_PREP_KIT, { showTableOfContents: true });
                                }
                            }}>
                                Back to Prepare
                            </button>
                        ) : (
                            <>
                                <button className="secondary-btn" onClick={() => onBackToConcepts && onBackToConcepts()}>
                                    Back to Concept List
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
